import {
  BalanceResponse,
  HubBalanceResponse,
  HubCurrencies,
} from "@letsbit/malcolmlb";
import BigNumber from "bignumber.js";
import { useGetMembers } from "hooks/partnerApi/useGetMembers";
import { useCurrencies } from "hooks/user/useCurrencies";
import { Wallets } from "hooks/user/useWallets";
import { useFormatter } from "hooks/utils/useFormatter";
import { ApiContext } from "providers/ApiProvider";
import { useContext, useEffect, useMemo, useState } from "react";

export function useConstructWallets({
  balances,
  preferredCurrency,
  currencies,
}: {
  balances?: BalanceResponse[];
  currencies?: HubCurrencies[];
  preferredCurrency?: string;
}): Wallets | undefined {
  const { formatValue } = useFormatter();
  const { publicInstance } = useContext(ApiContext);

  const [convertedBalances, setConvertedBalances] = useState<
    HubBalanceResponse[]
  >([]);

  const preferredCurrencyObj = currencies?.find(
    (c) => c.id === preferredCurrency,
  );

  useEffect(() => {
    if (!balances || !preferredCurrency || !currencies) return;
    const convertBalances = async () => {
      const [{ data: convertedBalance }, { data: convertedLocked }] =
        await Promise.all([
          publicInstance.hub.convertCurrencies({
            target_currency: preferredCurrency,
            value: balances.map((b) => {
              return {
                source_currency: b.currency,
                amount: b.balance,
              };
            }),
          }),
          publicInstance.hub.convertCurrencies({
            target_currency: preferredCurrency,
            value: balances.map((b) => {
              return {
                source_currency: b.currency,
                amount: b.locked,
              };
            }),
          }),
        ]);
      setConvertedBalances(
        balances.map((b) => ({
          ...b,
          balancePreferredCurrency:
            convertedBalance.find((c) => c.currency === b.currency)?.amount ||
            "0",
          lockedPreferredCurrency:
            convertedLocked.find((c) => c.currency === b.currency)?.amount ||
            "0",
        })),
      );
    };
    convertBalances();
  }, [currencies, balances, preferredCurrency, publicInstance]);

  return convertedBalances.reduce((prev: Wallets, balance) => {
    const currency = currencies?.find(
      (c) => c.id.toLowerCase() === balance.currency.toLowerCase(),
    );
    if (!currency) return prev;
    const total_balance = BigNumber.sum(
      balance.balance,
      balance.locked,
    ).toString();
    const total_balance_preferred_currency = BigNumber.sum(
      balance.balancePreferredCurrency,
      balance.lockedPreferredCurrency,
    ).toString();
    return {
      ...prev,
      [balance.currency]: {
        ...currency,
        ...balance,
        total_balance,
        total_balance_preferred_currency: formatValue(
          total_balance_preferred_currency,
          preferredCurrencyObj?.precision,
        ),
        total_balance_preferred_currency_without_format:
          total_balance_preferred_currency,
      },
    };
  }, {} as Wallets);
}

export function useMemberPlatformBalancePage(uid?: string) {
  const { data: members } = useGetMembers();

  const { memberPositions, name } = useMemo(() => {
    const member = members?.flat().find((member) => member.uid === uid);
    const memberPositions = member?.platform_positions || [];
    const memberName =
      member?.natural_person_data?.first_name +
        " " +
        member?.natural_person_data?.last_name || "";
    return {
      memberPositions: memberPositions,
      name: memberName,
    };
  }, [members, uid]);
  const { data: currencies } = useCurrencies({
    preferredCurrency: "usd",
    country: "AR",
  });

  const wallets = useConstructWallets({
    balances: memberPositions,
    currencies,
    preferredCurrency: "usd",
  });

  const parsedBalance = useMemo(() => {
    const walletArray = wallets ? Object.values(wallets) : [];
    const fiatWallets = walletArray.filter(
      (wallet) => wallet.type === "fiat" && wallet.tags.includes("fiat"),
    );
    const otherWallets = walletArray
      .filter(
        (wallet) =>
          +wallet.balance > 0 &&
          !fiatWallets.find((fiatWallet) => fiatWallet.id === wallet.id),
      )
      .sort(
        (a, b) => +b.balancePreferredCurrency - +a.balancePreferredCurrency,
      );
    const total = walletArray.reduce(
      (totalBalancePref, wallet) =>
        totalBalancePref + +wallet.balancePreferredCurrency,
      0,
    );
    return {
      fiatWallets,
      otherWallets,
      total,
    };
  }, [wallets]);

  return {
    ...parsedBalance,
    preferredCurrency: "usd",
    wallets: wallets,
    name,
    isLoading: !members || !currencies || !wallets,
  };
}
